import { Copy, Pencil, Plus, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { MarkdownLite } from "@/shared/components/MarkdownLite";
import type { CopilotoChatMessage } from "@/features/copiloto/CopilotoContext";
import { hasServerId, useCopiloto } from "@/features/copiloto/CopilotoContext";
import type { CopilotoMode } from "@/shared/api/api-client";
import { MestraIcon } from "@/features/copiloto/MestraIcon";

const THINKING_PHRASES = [
  "Pensando…",
  "Consultando dados da plataforma…",
  "Organizando a resposta…"
];

function ThinkingIcon({ phase }: { phase: number }) {
  if (phase === 1) {
    return (
      <span className="chat-status-search relative inline-flex items-center justify-center w-4 h-4 rounded-full border border-[#CBD5E1] shrink-0 align-middle">
        <span className="absolute w-1.5 h-1.5 rounded-full border border-[#94A3B8]" />
      </span>
    );
  }

  if (phase === 2) {
    return (
      <span className="inline-flex flex-col justify-center gap-[2.5px] w-4 h-4 shrink-0 align-middle">
        <span className="chat-status-line h-px rounded-full bg-[#94A3B8] w-3 block" />
        <span className="chat-status-line h-px rounded-full bg-[#94A3B8] w-2.5 block" style={{ animationDelay: "140ms" }} />
        <span className="chat-status-line h-px rounded-full bg-[#94A3B8] w-3.5 block" style={{ animationDelay: "280ms" }} />
      </span>
    );
  }

  return (
    <span className="chat-status-spark relative inline-flex items-center justify-center w-4 h-4 shrink-0 align-middle">
      <MestraIcon size={14} className="text-[#94A3B8]" />
    </span>
  );
}

function ThinkingStatus() {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setPhase(p => (p + 1) % THINKING_PHRASES.length), 1600);
    return () => clearInterval(id);
  }, []);

  return (
    <span className="copiloto-thinking-status">
      <ThinkingIcon phase={phase} />
      <span>{THINKING_PHRASES[phase]}</span>
    </span>
  );
}

const FOLLOWUP_SUGGESTIONS = [
  { label: "Explique melhor", instruction: "Explique a resposta anterior em mais detalhes, de forma simples e didática." },
  { label: "Dê um exemplo", instruction: "Dê um exemplo prático relacionado à resposta anterior." },
  { label: "Resuma", instruction: "Resuma a resposta anterior de forma bem objetiva, em poucos tópicos." },
  { label: "Mostre fontes", instruction: "Detalhe quais dados internos da plataforma embasaram a resposta anterior." }
];

function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return { copied, copy };
}

function UserMessage({ message, disabled }: { message: CopilotoChatMessage; disabled: boolean }) {
  const { editMessage } = useCopiloto();
  const { copied, copy } = useCopyToClipboard();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.content);
  const editRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!editing) return;
    editRef.current?.focus();
    editRef.current?.setSelectionRange(draft.length, draft.length);
  }, [editing]);

  const startEdit = () => {
    setDraft(message.content);
    setEditing(true);
  };

  const saveEdit = () => {
    const clean = draft.trim();
    if (!clean) return;
    setEditing(false);
    void editMessage(message.id, clean);
  };

  if (editing) {
    return (
      <div className="copiloto-message copiloto-message-user is-editing">
        <div className="copiloto-edit-box">
          <textarea
            ref={editRef}
            value={draft}
            onChange={event => setDraft(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                saveEdit();
              }
              if (event.key === "Escape") setEditing(false);
            }}
            rows={Math.min(8, Math.max(1, draft.split("\n").length))}
          />
          <div className="copiloto-edit-actions">
            <button type="button" className="copiloto-btn-cancel" onClick={() => setEditing(false)}>
              Cancelar
            </button>
            <button type="button" className="copiloto-btn-save" disabled={!draft.trim()} onClick={saveEdit}>
              Salvar e reenviar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="copiloto-message copiloto-message-user group">
      <div className="copiloto-message-bubble">
        <p>{message.content}</p>
      </div>
      <div className="copiloto-message-actions">
        <button type="button" onClick={() => void copy(message.content)}>
          <Copy size={12}/> <span>{copied ? "Copiado" : "Copiar"}</span>
        </button>
        {/* Pergunta sem id do servidor (o envio falhou) não se edita: o
            PUT iria no caminho com um id `local-…` que não existe
            (2026-10-09, teste de render). */}
        <button type="button" disabled={disabled || !hasServerId(message)} onClick={startEdit}>
          <Pencil size={12}/> <span>Editar</span>
        </button>
      </div>
    </div>
  );
}

function AssistantMessage({
  message,
  mode,
  disabled
}: {
  message: CopilotoChatMessage;
  mode: CopilotoMode;
  disabled: boolean;
}) {
  const { copied, copy } = useCopyToClipboard();
  const { sendMessage, regenerateMessage } = useCopiloto();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const isStreaming = message.pending && Boolean(message.content) && !message.error;
  const hasContent = Boolean(message.content.trim());

  const currentMode = message.mode ?? mode;

  return (
    <div className="copiloto-message copiloto-message-assistant group">
      {message.pending && !message.content ? (
        <ThinkingStatus />
      ) : message.error ? (
        <p className="copiloto-message-error">{message.content}</p>
      ) : !hasContent ? (
        <p className="copiloto-message-empty">Não recebi uma resposta para esta pergunta.</p>
      ) : (
        <div className="copiloto-message-text">
          <MarkdownLite content={message.content}/>
          {isStreaming && <span className="copiloto-cursor" aria-hidden="true"/>}
        </div>
      )}
      {!message.pending && hasContent && !message.error && (
        <div className="copiloto-assistant-actions-wrap">
          <div className="copiloto-assistant-actions">
            <button type="button" className="copiloto-action-btn" onClick={() => void copy(message.content)}>
              <Copy size={13}/> <span>{copied ? "Copiado" : "Copiar"}</span>
            </button>
            <button
              type="button"
              className="copiloto-action-btn"
              disabled={disabled}
              onClick={() => void regenerateMessage(message.id, currentMode)}
            >
              <RefreshCw size={13}/> <span>Gerar outra resposta</span>
            </button>
            <button
              type="button"
              className={`copiloto-action-plus ${showSuggestions ? "is-active" : ""}`}
              disabled={disabled}
              onClick={() => setShowSuggestions(v => !v)}
              title="Mais opções"
              aria-label="Mais opções"
            >
              <Plus size={14} className={showSuggestions ? "rotate-45" : ""} />
            </button>
          </div>
          {showSuggestions && (
            <div className="copiloto-followup-chips">
              {FOLLOWUP_SUGGESTIONS.map(s => (
                <button
                  key={s.label}
                  type="button"
                  className="copiloto-chip-btn"
                  disabled={disabled}
                  onClick={() => {
                    void sendMessage(s.instruction, currentMode, { visible: false });
                    setShowSuggestions(false);
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ChatThread({
  messages,
  mode = "padrao",
  disabled = false
}: {
  messages: CopilotoChatMessage[];
  mode?: CopilotoMode;
  disabled?: boolean;
}) {
  return (
    <div className="copiloto-thread">
      {messages.filter(message => !message.hidden).map(message =>
        message.role === "user"
          ? <UserMessage key={message.id} message={message} disabled={disabled}/>
          : <AssistantMessage key={message.id} message={message} mode={mode} disabled={disabled}/>
      )}
    </div>
  );
}
